import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './EnhancedHome.module.css';
import Navigation from '../Home/Navigation';
import VideoGallery from './sections/VideoGallery';
import StickyCtaButton from './sections/StickyCtaButton';

const EnhancedHome = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [userCount, setUserCount] = useState(4247);
  const [showStickyButton, setShowStickyButton] = useState(false);

  // Animate user count
  useEffect(() => {
    const interval = setInterval(() => {
      setUserCount(prev => prev + Math.floor(Math.random() * 3));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Sticky button on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyButton(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      id: 'content-studio',
      title: t('features.contentStudio.title'),
      icon: '✨',
      description: t('features.contentStudio.description'),
      action: () => navigate("/create-content"),
      aiModel: t('features.contentStudio.aiModel'),
      comingSoon: false,
      category: 'creation'
    },
    {
      id: 'video-editor',
      title: t('features.videoEditor.title'),
      icon: '🎞️',
      description: t('features.videoEditor.description'),
      action: () => navigate("/ai-video-editor"),
      aiModel: t('features.videoEditor.aiModel'),
      comingSoon: false,
      category: 'edit'
    },
    {
      id: 'script-generator',
      title: t('features.scriptGenerator.title'),
      icon: '🎬',
      description: t('features.scriptGenerator.description'),
      action: () => navigate("/script-generator"),
      aiModel: t('features.scriptGenerator.aiModel'),
      comingSoon: false,
      category: 'creation'
    },
    {
      id: 'prompts',
      title: t('features.promptGenerator.title'),
      icon: '🎯',
      description: t('features.promptGenerator.description'),
      action: () => navigate("/prompt-generator"),
      aiModel: t('features.promptGenerator.aiModel'),
      comingSoon: false,
      category: 'creation'
    },
    {
      id: 'audio',
      title: t('features.audioGenerator.title'),
      icon: '🎵',
      description: t('features.audioGenerator.description'),
      action: () => navigate("/audio-generation"),
      aiModel: t('features.audioGenerator.aiModel'),
      comingSoon: false,
      category: 'creation'
    },
    {
      id: 'captions',
      title: t('features.captions.title'),
      icon: '📝',
      description: t('features.captions.description'),
      action: () => navigate("/Caption-LandingPage"),
      aiModel: t('features.captions.aiModel'),
      comingSoon: false,
      category: 'edit'
    },
    {
      id: 'sound-generation',
      title: t('features.soundtrack.title'),
      icon: '🎼',
      description: t('features.soundtrack.description'),
      action: () => navigate("/sound-generation"),
      aiModel: t('features.soundtrack.aiModel'),
      comingSoon: false,
      category: 'creation'
    },
    {
      id: 'titles',
      title: t('features.titles.title'),
      icon: '✍️',
      description: t('features.titles.description'),
      action: () => navigate("/title-landing"),
      aiModel: t('features.titles.aiModel'),
      comingSoon: false,
      category: 'edit'
    },
    {
      id: 'thumbnails',
      title: t('features.thumbnails.title'),
      icon: '🖼️',
      description: t('features.thumbnails.description'),
      action: () => navigate("/thumbnail-landing"),
      aiModel: t('features.thumbnails.aiModel'),
      comingSoon: false,
      category: 'creation'
    },
    {
      id: 'social',
      title: t('features.social.title'),
      icon: '📤',
      description: t('features.social.description'),
      action: () => navigate("/social-media"),
      aiModel: t('features.social.aiModel'),
      comingSoon: false,
      category: 'edit'
    }
  ];

  const testimonials = [
    {
      name: t('testimonials.sarah.name'),
      role: t('testimonials.sarah.role'),
      avatar: "👩‍💼",
      quote: t('testimonials.sarah.quote'),
      metric: t('testimonials.sarah.metric')
    },
    {
      name: t('testimonials.mike.name'),
      role: t('testimonials.mike.role'),
      avatar: "👨‍🏫",
      quote: t('testimonials.mike.quote'),
      metric: t('testimonials.mike.metric')
    },
    {
      name: t('testimonials.alex.name'),
      role: t('testimonials.alex.role'),
      avatar: "👨‍💻",
      quote: t('testimonials.alex.quote'),
      metric: t('testimonials.alex.metric')
    }
  ];

  const stats = [
    { number: "10", label: t('hero.stats.apps'), suffix: "" },
    { number: "150", label: t('hero.stats.savings'), suffix: "+" },
    { number: "16", label: t('hero.stats.minutes'), suffix: "" },
    // { number: userCount.toLocaleString(), label: t('hero.stats.users'), suffix: "" }
  ];

  return (
    <div className={styles.container}>
      <Navigation />
      
      {/* STICKY CTA BUTTON - NEW */}
      <StickyCtaButton show={showStickyButton} />
      
      {/* NEW CLEAN HERO SECTION - Value Focused & Benefit-Driven */}
      <section className={styles.newCleanHero}>
        <div className={styles.newHeroContent}>
          
          {/* TOP: Video on Right, Text on Left */}
          <div className={styles.heroTopRow}>
            {/* LEFT SIDE: Text Content */}
            <div className={styles.heroTextSection}>
              {/* IMPROVED HEADLINE - Concrete benefit + Who it's for */}
              <h1 className={styles.newHeroTitle}>
                {t('hero.title')}<br />
                <span className={styles.heroSubBrand}>No Tool Juggling. Text-to-Publish. All in One.</span>
              </h1>
              
              {/* IMPROVED SUBHEADLINE - Clear value proposition */}
              <p className={styles.newHeroSubtitle}>
                {t('hero.subtitle')}
              </p>
               {/* MIDDLE: Benefits Row (ONE LINE) */}
          <div className={styles.heroBenefitsRowHorizontal}>
            <div className={styles.heroBenefit}>
              <span className={styles.benefitIcon}>⚡</span>
              <span className={styles.benefitText}>{t('hero.benefits.timeSaving')}</span>
            </div>
            <div className={styles.heroBenefit}>
              <span className={styles.benefitIcon}>💰</span>
              <span className={styles.benefitText}>{t('hero.benefits.toolCount')}</span>
            </div>
            <div className={styles.heroBenefit}>
              <span className={styles.benefitIcon}>✨</span>
              <span className={styles.benefitText}>{t('hero.benefits.capability')}</span>
            </div>
          </div>
            </div>

            {/* RIGHT SIDE: Demo Video */}
            <div className={styles.heroVideoSection}>
              <div className={styles.heroDemoPlaceholder}>
                <video
                  width="100%"
                  height="auto"
                  autoPlay
                  loop
                  muted
                  style={{
                    borderRadius: '12px',
                    display: 'block',
                    width: '100%',
                    height: 'auto'
                  }}
                  src= { i18n.language === 'fr'? "https://aiapptest2.s3.us-east-1.amazonaws.com/projects/public/lp_1.mov": "https://aiapptest2.s3.us-east-1.amazonaws.com/projects/public/lp_2.mov"}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        </div>

        {/* CENTERED BELOW: CTA Buttons and Social Proof */}
        <div className={styles.heroCTASection}>
          <div className={styles.newHeroCTA}>
            <button 
              className={styles.newPrimaryButton}
              onClick={() => navigate('/create-content')}
            >
              {t('hero.cta')}
            </button>
            <button 
              className={styles.newSecondaryButton}
              onClick={() => navigate('/features')}
            >
              {t('hero.ctaSecondary')}
            </button>
          </div>
          
          {/* Social proof - Trust signals */}
          <div className={styles.newHeroProof}>
            <span>✓ {t('hero.noCreditCard')}</span>
            <span>✓ {userCount.toLocaleString()}+ {t('hero.creatorsCount')}</span>
            <span>✓ {t('hero.moneyBackGuarantee')}</span>
          </div>
        </div>

      </section>

      {/* NEW USE CASES SECTION - Customer Focused */}
      <section className={styles.newUseCasesSection}>
        <h2 className={styles.newSectionTitle}>{t('useCases.sectionTitle')}</h2>
        <p className={styles.newSectionSubtitle}>{t('useCases.sectionSubtitle')}</p>
        
        <div className={styles.newUseCasesGrid}>
          {/* Product Launches */}
          <div className={styles.newUseCaseCard}>
            <div className={styles.newUseCaseHeader}>
              <span className={styles.newUseCaseEmoji}>🎬</span>
              <h3>{t('useCases.productLaunch.title')}</h3>
            </div>
            <p className={styles.newUseCaseDescription}>
              {t('useCases.productLaunch.description')}
            </p>
            <div className={styles.newUseCaseTransform}>
              <div className={styles.beforeState}>
                <strong>{t('useCases.productLaunch.beforeTitle')}</strong>
                <ul>
                  <li>{t('useCases.productLaunch.before1')}</li>
                  <li>{t('useCases.productLaunch.before2')}</li>
                  <li>{t('useCases.productLaunch.before3')}</li>
                  <li>{t('useCases.productLaunch.before4')}</li>
                </ul>
              </div>
              <div className={styles.afterState}>
                <strong>{t('useCases.productLaunch.afterTitle')}</strong>
                <ul>
                  <li>{t('useCases.productLaunch.after1')}</li>
                  <li>{t('useCases.productLaunch.after2')}</li>
                  <li>{t('useCases.productLaunch.after3')}</li>
                  <li>{t('useCases.productLaunch.after4')}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Social Media Content */}
          <div className={styles.newUseCaseCard}>
            <div className={styles.newUseCaseHeader}>
              <span className={styles.newUseCaseEmoji}>📱</span>
              <h3>{t('useCases.socialMedia.title')}</h3>
            </div>
            <p className={styles.newUseCaseDescription}>
              {t('useCases.socialMedia.description')}
            </p>
            <div className={styles.newUseCaseTransform}>
              <div className={styles.beforeState}>
                <strong>{t('useCases.socialMedia.beforeTitle')}</strong>
                <ul>
                  <li>{t('useCases.socialMedia.before1')}</li>
                  <li>{t('useCases.socialMedia.before2')}</li>
                  <li>{t('useCases.socialMedia.before3')}</li>
                  <li>{t('useCases.socialMedia.before4')}</li>
                </ul>
              </div>
              <div className={styles.afterState}>
                <strong>{t('useCases.socialMedia.afterTitle')}</strong>
                <ul>
                  <li>{t('useCases.socialMedia.after1')}</li>
                  <li>{t('useCases.socialMedia.after2')}</li>
                  <li>{t('useCases.socialMedia.after3')}</li>
                  <li>{t('useCases.socialMedia.after4')}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Online Courses */}
          <div className={styles.newUseCaseCard}>
            <div className={styles.newUseCaseHeader}>
              <span className={styles.newUseCaseEmoji}>📚</span>
              <h3>{t('useCases.onlineCourse.title')}</h3>
            </div>
            <p className={styles.newUseCaseDescription}>
              {t('useCases.onlineCourse.description')}
            </p>
            <div className={styles.newUseCaseTransform}>
              <div className={styles.beforeState}>
                <strong>{t('useCases.onlineCourse.beforeTitle')}</strong>
                <ul>
                  <li>{t('useCases.onlineCourse.before1')}</li>
                  <li>{t('useCases.onlineCourse.before2')}</li>
                  <li>{t('useCases.onlineCourse.before3')}</li>
                  <li>{t('useCases.onlineCourse.before4')}</li>
                </ul>
              </div>
              <div className={styles.afterState}>
                <strong>{t('useCases.onlineCourse.afterTitle')}</strong>
                <ul>
                  <li>{t('useCases.onlineCourse.after1')}</li>
                  <li>{t('useCases.onlineCourse.after2')}</li>
                  <li>{t('useCases.onlineCourse.after3')}</li>
                  <li>{t('useCases.onlineCourse.after4')}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Marketing Teams */}
          <div className={styles.newUseCaseCard}>
            <div className={styles.newUseCaseHeader}>
              <span className={styles.newUseCaseEmoji}>💼</span>
              <h3>{t('useCases.marketingTeam.title')}</h3>
            </div>
            <p className={styles.newUseCaseDescription}>
              {t('useCases.marketingTeam.description')}
            </p>
            <div className={styles.newUseCaseTransform}>
              <div className={styles.beforeState}>
                <strong>{t('useCases.marketingTeam.beforeTitle')}</strong>
                <ul>
                  <li>{t('useCases.marketingTeam.before1')}</li>
                  <li>{t('useCases.marketingTeam.before2')}</li>
                  <li>{t('useCases.marketingTeam.before3')}</li>
                  <li>{t('useCases.marketingTeam.before4')}</li>
                </ul>
              </div>
              <div className={styles.afterState}>
                <strong>{t('useCases.marketingTeam.afterTitle')}</strong>
                <ul>
                  <li>{t('useCases.marketingTeam.after1')}</li>
                  <li>{t('useCases.marketingTeam.after2')}</li>
                  <li>{t('useCases.marketingTeam.after3')}</li>
                  <li>{t('useCases.marketingTeam.after4')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW USE CASE APPLICATIONS TILES SECTION */}
      <section className={styles.useCaseApplicationsSection}>
        <h2 className={styles.newSectionTitle}>{t('applications.sectionTitle')}</h2>
        <p className={styles.newSectionSubtitle}>{t('applications.sectionSubtitle')}</p>
        
        <div className={styles.applicationTilesGrid}>
          {/* Video Ads */}
          <div className={styles.applicationTile}>
            <div className={styles.tileIcon}>📺</div>
            <h3>{t('applications.videoAds.title')}</h3>
            <p>{t('applications.videoAds.description')}</p>
            <div className={styles.tileExample}>
              <strong>{t('applications.exampleLabel')}</strong> "{t('applications.videoAds.example')}"
            </div>
          </div>

          {/* Mini Movies */}
          <div className={styles.applicationTile}>
            <div className={styles.tileIcon}>🎥</div>
            <h3>{t('applications.miniMovies.title')}</h3>
            <p>{t('applications.miniMovies.description')}</p>
            <div className={styles.tileExample}>
              <strong>{t('applications.exampleLabel')}</strong> "{t('applications.miniMovies.example')}"
            </div>
          </div>

          {/* Bible/Faith Videos */}
          <div className={styles.applicationTile}>
            <div className={styles.tileIcon}>✝️</div>
            <h3>{t('applications.faithVideos.title')}</h3>
            <p>{t('applications.faithVideos.description')}</p>
            <div className={styles.tileExample}>
              <strong>{t('applications.exampleLabel')}</strong> "{t('applications.faithVideos.example')}"
            </div>
          </div>

          {/* Scientific Explainers */}
          <div className={styles.applicationTile}>
            <div className={styles.tileIcon}>🔬</div>
            <h3>{t('applications.scientificExplainers.title')}</h3>
            <p>{t('applications.scientificExplainers.description')}</p>
            <div className={styles.tileExample}>
              <strong>{t('applications.exampleLabel')}</strong> "{t('applications.scientificExplainers.example')}"
            </div>
          </div>

          {/* Content Creator Videos */}
          <div className={styles.applicationTile}>
            <div className={styles.tileIcon}>🎬</div>
            <h3>{t('applications.creatorContent.title')}</h3>
            <p>{t('applications.creatorContent.description')}</p>
            <div className={styles.tileExample}>
              <strong>{t('applications.exampleLabel')}</strong> "{t('applications.creatorContent.example')}"
            </div>
          </div>

          {/* Explainer Videos */}
          <div className={styles.applicationTile}>
            <div className={styles.tileIcon}>💡</div>
            <h3>{t('applications.businessExplainers.title')}</h3>
            <p>{t('applications.businessExplainers.description')}</p>
            <div className={styles.tileExample}>
              <strong>{t('applications.exampleLabel')}</strong> "{t('applications.businessExplainers.example')}"
            </div>
          </div>

          {/* Educational Content */}
          <div className={styles.applicationTile}>
            <div className={styles.tileIcon}>🎓</div>
            <h3>{t('applications.educationalCourses.title')}</h3>
            <p>{t('applications.educationalCourses.description')}</p>
            <div className={styles.tileExample}>
              <strong>{t('applications.exampleLabel')}</strong> "{t('applications.educationalCourses.example')}"
            </div>
          </div>

          {/* Internal Communications */}
          <div className={styles.applicationTile}>
            <div className={styles.tileIcon}>📊</div>
            <h3>{t('applications.teamUpdates.title')}</h3>
            <p>{t('applications.teamUpdates.description')}</p>
            <div className={styles.tileExample}>
              <strong>{t('applications.exampleLabel')}</strong> "{t('applications.teamUpdates.example')}"
            </div>
          </div>

          {/* Real Estate Tours */}
          <div className={styles.applicationTile}>
            <div className={styles.tileIcon}>🏠</div>
            <h3>{t('applications.propertyTours.title')}</h3>
            <p>{t('applications.propertyTours.description')}</p>
            <div className={styles.tileExample}>
              <strong>{t('applications.exampleLabel')}</strong> "{t('applications.propertyTours.example')}"
            </div>
          </div>

          {/* Recipe & Cooking */}
          <div className={styles.applicationTile}>
            <div className={styles.tileIcon}>👨‍🍳</div>
            <h3>{t('applications.recipeVideos.title')}</h3>
            <p>{t('applications.recipeVideos.description')}</p>
            <div className={styles.tileExample}>
              <strong>{t('applications.exampleLabel')}</strong> "{t('applications.recipeVideos.example')}"
            </div>
          </div>

          {/* Fitness & Wellness */}
          <div className={styles.applicationTile}>
            <div className={styles.tileIcon}>💪</div>
            <h3>{t('applications.fitnessContent.title')}</h3>
            <p>{t('applications.fitnessContent.description')}</p>
            <div className={styles.tileExample}>
              <strong>{t('applications.exampleLabel')}</strong> "{t('applications.fitnessContent.example')}"
            </div>
          </div>

          {/* News & Commentary */}
          <div className={styles.applicationTile}>
            <div className={styles.tileIcon}>📰</div>
            <h3>{t('applications.newsAnalysis.title')}</h3>
            <p>{t('applications.newsAnalysis.description')}</p>
            <div className={styles.tileExample}>
              <strong>{t('applications.exampleLabel')}</strong> "{t('applications.newsAnalysis.example')}"
            </div>
          </div>
        </div>

        <div className={styles.applicationsCTA}>
          <p className={styles.applicationsCTAText}>{t('applications.ctaText')}</p>
          <button 
            className={styles.newPrimaryButton}
            onClick={() => navigate('/create-content')}
          >
            {t('applications.ctaButton')}
          </button>
        </div>
      </section>

      {/* NEW WORKFLOW TRANSFORMATION SECTION */}
      <section className={styles.newWorkflowSection}>
        <h2 className={styles.newSectionTitle}>{t('workflowTransform.sectionTitle')}</h2>
        
        <div className={styles.newComparisonLayout}>
          {/* Old Way */}
          <div className={styles.newOldWay}>
            <h3 className={styles.newOldWayTitle}>{t('workflowTransform.oldWay.title')}</h3>
            <div className={styles.newTimeline}>
              <div className={styles.newTimelineItem}>
                <div className={styles.newDayLabel}>{t('workflowTransform.oldWay.day1.label')}</div>
                <div className={styles.newTaskDescription}>
                  <strong>{t('workflowTransform.oldWay.day1.task')}</strong>
                  <p>{t('workflowTransform.oldWay.day1.description')}</p>
                  <span className={styles.newTimeAmount}>{t('workflowTransform.oldWay.day1.time')}</span>
                </div>
              </div>
              <div className={styles.newTimelineItem}>
                <div className={styles.newDayLabel}>{t('workflowTransform.oldWay.day2.label')}</div>
                <div className={styles.newTaskDescription}>
                  <strong>{t('workflowTransform.oldWay.day2.task')}</strong>
                  <p>{t('workflowTransform.oldWay.day2.description')}</p>
                  <span className={styles.newTimeAmount}>{t('workflowTransform.oldWay.day2.time')}</span>
                </div>
              </div>
              <div className={styles.newTimelineItem}>
                <div className={styles.newDayLabel}>{t('workflowTransform.oldWay.day3.label')}</div>
                <div className={styles.newTaskDescription}>
                  <strong>{t('workflowTransform.oldWay.day3.task')}</strong>
                  <p>{t('workflowTransform.oldWay.day3.description')}</p>
                  <span className={styles.newTimeAmount}>{t('workflowTransform.oldWay.day3.time')}</span>
                </div>
              </div>
              <div className={styles.newTimelineItem}>
                <div className={styles.newDayLabel}>{t('workflowTransform.oldWay.day4.label')}</div>
                <div className={styles.newTaskDescription}>
                  <strong>{t('workflowTransform.oldWay.day4.task')}</strong>
                  <p>{t('workflowTransform.oldWay.day4.description')}</p>
                  <span className={styles.newTimeAmount}>{t('workflowTransform.oldWay.day4.time')}</span>
                </div>
              </div>
              <div className={styles.newTimelineItem}>
                <div className={styles.newDayLabel}>{t('workflowTransform.oldWay.day5.label')}</div>
                <div className={styles.newTaskDescription}>
                  <strong>{t('workflowTransform.oldWay.day5.task')}</strong>
                  <p>{t('workflowTransform.oldWay.day5.description')}</p>
                  <span className={styles.newTimeAmount}>{t('workflowTransform.oldWay.day5.time')}</span>
                </div>
              </div>
            </div>
            <div className={styles.newTotalTime}>
              <strong>{t('workflowTransform.oldWay.total')}</strong>
              <p>{t('workflowTransform.oldWay.toolCost')}</p>
            </div>
          </div>

          {/* New Way */}
          <div className={styles.newNewWay}>
            <h3 className={styles.newNewWayTitle}>{t('workflowTransform.newWay.title')}</h3>
            <div className={styles.newTimeline}>
              <div className={styles.newTimelineItem}>
                <div className={styles.newMinuteLabel}>{t('workflowTransform.newWay.step1.label')}</div>
                <div className={styles.newTaskDescription}>
                  <strong>{t('workflowTransform.newWay.step1.task')}</strong>
                  <p>{t('workflowTransform.newWay.step1.description')}</p>
                  <span className={styles.newTimeAmount}>{t('workflowTransform.newWay.step1.time')}</span>
                </div>
              </div>
              <div className={styles.newTimelineItem}>
                <div className={styles.newMinuteLabel}>{t('workflowTransform.newWay.step2.label')}</div>
                <div className={styles.newTaskDescription}>
                  <strong>{t('workflowTransform.newWay.step2.task')}</strong>
                  <p>{t('workflowTransform.newWay.step2.description')}</p>
                  <span className={styles.newTimeAmount}>{t('workflowTransform.newWay.step2.time')}</span>
                </div>
              </div>
              <div className={styles.newTimelineItem}>
                <div className={styles.newMinuteLabel}>{t('workflowTransform.newWay.step3.label')}</div>
                <div className={styles.newTaskDescription}>
                  <strong>{t('workflowTransform.newWay.step3.task')}</strong>
                  <p>{t('workflowTransform.newWay.step3.description')}</p>
                  <span className={styles.newTimeAmount}>{t('workflowTransform.newWay.step3.time')}</span>
                </div>
              </div>
              <div className={styles.newTimelineItem}>
                <div className={styles.newMinuteLabel}>{t('workflowTransform.newWay.step4.label')}</div>
                <div className={styles.newTaskDescription}>
                  <strong>{t('workflowTransform.newWay.step4.task')}</strong>
                  <p>{t('workflowTransform.newWay.step4.description')}</p>
                  <span className={styles.newTimeAmount}>{t('workflowTransform.newWay.step4.time')}</span>
                </div>
              </div>
              <div className={styles.newTimelineItem}>
                <div className={styles.newMinuteLabel}>{t('workflowTransform.newWay.step5.label')}</div>
                <div className={styles.newTaskDescription}>
                  <strong>{t('workflowTransform.newWay.step5.task')}</strong>
                  <p>{t('workflowTransform.newWay.step5.description')}</p>
                  <span className={styles.newTimeAmount}>{t('workflowTransform.newWay.step5.time')}</span>
                </div>
              </div>
            </div>
            <div className={styles.newTotalTime}>
              <strong>{t('workflowTransform.newWay.total')}</strong>
              <p>{t('workflowTransform.newWay.toolCost')}</p>
            </div>
          </div>
        </div>

        <div className={styles.newImpactStatement}>
          <p className={styles.newImpactText}>
            {t('workflowTransform.impactStatement')}<br />
            <strong>{t('workflowTransform.impactEmphasis')}</strong>
          </p>
          <button 
            className={styles.newPrimaryButton}
            onClick={() => navigate('/create-content')}
          >
            {t('workflowTransform.ctaButton')}
          </button>
        </div>
      </section>

      {/* ORIGINAL HERO SECTION STARTS HERE */}
      {/* Hero Section */}
      {/* <section className={styles.hero}>
        <div className={styles.heroBackground}>
          <div className={styles.gradientOrb}></div>
          <div className={styles.gradientOrb}></div>
        </div>
        
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <div className={styles.badge}>
              🚀 FOUNDER PRICING: Join {userCount.toLocaleString()}+ Content Professionals
            </div>
            
            <h1 className={styles.heroTitle}>
              <span className={styles.gradient}>Stop Spending Hours on One Video</span><br />
              <span className={styles.gradient}>9 AI Tools.</span><br />
              <small style={{fontSize: '0.7em', opacity: 0.9}}>Complete Content Creation Platform for Professionals</small>
            </h1>
            
            <p className={styles.heroDescription}>
              Create professional video content in 16 minutes - Complete 9-step workflow from idea to published without recording. 
              Script writing, video generation, captions, thumbnails, titles, and direct social publishing.
              <strong> FOUNDER PRICING: Only first 100 subscribers get this rate!</strong>
            </p>
            
            <div className={styles.valueProps}>
              <div className={styles.valueProp}>
                <span className={styles.checkIcon}>✅</span>
                <span><strong>Complete Content Creation:</strong> Idea → Video → Social Media in 16 minutes</span>
              </div>
              <div className={styles.valueProp}>
                <span className={styles.checkIcon}>✅</span>
                <span><strong>Replace Multiple Tools:</strong> One platform vs $200+ monthly tool subscriptions</span>
              </div>
              <div className={styles.valueProp}>
                <span className={styles.checkIcon}>✅</span>
                <span><strong>Professional Quality:</strong> AI-powered workflow for marketing teams and creators</span>
              </div>
            </div>
            
            <div className={styles.heroActions}>
              <button 
                className={styles.primaryButton}
                onClick={() => navigate('/create-content')}
              >
                Start Your 16-Minute Workflow - No Credit Card
              </button>
              <button 
                className={styles.secondaryButton}
                onClick={() => navigate('/features')}
              >
                See All 9 Tools →
              </button>
            </div>
            
            <div className={styles.trustSignals}>
              <span>🔒 SOC 2 Compliant</span>
              <span>⚡ 99.9% Uptime</span>
              <span>💰 30-Day Money Back</span>
            </div>
          </div>
          
          {/* VIDEO SECTION - COMMENTED OUT UNTIL WE HAVE A VIDEO */}
          {/* <div className={styles.heroVisual}>
            <div className={styles.videoPlaceholder}>
              <div className={styles.videoContainer}>
                <div className={styles.playButton}>
                  <div className={styles.playIcon}>▶</div>
                </div>
                <div className={styles.videoOverlay}>
                  <div className={styles.videoTitle}>See Our Platform in Action</div>
                  <div className={styles.videoDuration}>2:30</div>
                </div>
                <div className={styles.videoPreview}>
                  <div className={styles.previewText}>
                    Watch how content professionals are transforming their workflow with AI-powered tools
                  </div>
                </div>
              </div>
            </div>
            
            <div className={styles.demoContainer}>
              <div className={styles.demoHeader}>
                <div className={styles.windowControls}>
                  <div className={styles.control}></div>
                  <div className={styles.control}></div>
                  <div className={styles.control}></div>
                </div>
                <span>Kingdom Studio Platform</span>
              </div>
              <div className={styles.demoContent}>
                <div className={styles.demoFeature}>
                  <span className={styles.demoIcon}>🎯</span>
                  <div>
                    <strong>AI Script Generated:</strong><br />
                    "Create a 5-minute educational video about productivity tips..."
                  </div>
                </div>
                <div className={styles.demoFeature}>
                  <span className={styles.demoIcon}>🎵</span>
                  <div>
                    <strong>Audio Generated:</strong><br />
                    Professional narration with engaging background music
                  </div>
                </div>
                <div className={styles.demoFeature}>
                  <span className={styles.demoIcon}>📤</span>
                  <div>
                    <strong>Ready to Publish:</strong><br />
                    Upload directly to YouTube, Instagram, Facebook
                  </div>
                </div>
              </div>
            </div>
          </div> 
        
        End of video heroVisual section
        </div>
      </section> 
      END OF HERO SECTION - All commented out */}

      {/* Value Breakdown Section - COMMENTED OUT */}
      {/* <section className={styles.valueBreakdown}>
        <div className={styles.valueContent}>
          <h3>What You Get for $19.99/Month:</h3>
          <div className={styles.valueGrid}>
            <div className={styles.valueItem}>
              <div className={styles.valueIcon}>🎬</div>
              <h4>AI Script Generator</h4>
              <p>Complete professional content scripts with marketing context</p>
              <div className={styles.valuePrice}>Worth: $49/mo</div>
            </div>
            <div className={styles.valueItem}>
              <div className={styles.valueIcon}>🎯</div>
              <h4>Script → Prompt Converter</h4>
              <p>Automatic conversion to optimized video prompts</p>
              <div className={styles.valuePrice}>Worth: $19/mo</div>
            </div>
            <div className={styles.valueItem}>
              <div className={styles.valueIcon}>🎵</div>
              <h4>AI Audio Generator</h4>
              <p>Professional narration with multiple voice options</p>
              <div className={styles.valuePrice}>Worth: $22/mo</div>
            </div>
            <div className={styles.valueItem}>
              <div className={styles.valueIcon}>🎥</div>
              <h4>AI Video Generator</h4>
              <p>Veo 3 powered video creation from scripts</p>
              <div className={styles.valuePrice}>Worth: $30/mo</div>
            </div>
            <div className={styles.valueItem}>
              <div className={styles.valueIcon}>📝</div>
              <h4>AI Caption Generator</h4>
              <p>Professional captions with 12+ styling options</p>
              <div className={styles.valuePrice}>Worth: $15/mo</div>
            </div>
            <div className={styles.valueItem}>
              <div className={styles.valueIcon}>🔊</div>
              <h4>AI Sound Generator</h4>
              <p>Background music and sound effects</p>
              <div className={styles.valuePrice}>Worth: $12/mo</div>
            </div>
            <div className={styles.valueItem}>
              <div className={styles.valueIcon}>🖼️</div>
              <h4>AI Thumbnail Creator</h4>
              <p>Eye-catching thumbnails with AI generation</p>
              <div className={styles.valuePrice}>Worth: $9/mo</div>
            </div>
            <div className={styles.valueItem}>
              <div className={styles.valueIcon}>✍️</div>
              <h4>Title & Description AI</h4>
              <p>SEO-optimized titles and descriptions</p>
              <div className={styles.valuePrice}>Worth: $7/mo</div>
            </div>
            <div className={styles.valueItem}>
              <div className={styles.valueIcon}>📤</div>
              <h4>Social Media Publisher</h4>
              <p>Direct publishing to YouTube, Instagram, Facebook</p>
              <div className={styles.valuePrice}>Worth: $15/mo</div>
            </div>
          </div>
          <div className={styles.totalValue}>
            <div className={styles.totalBreakdown}>
              <div className={styles.totalLine}>
                <span>Total Value if Purchased Separately:</span>
                <span className={styles.totalPrice}>$178/month</span>
              </div>
              <div className={styles.totalLine}>
                <span>Your Founder Price:</span>
                <span className={styles.founderPrice}>$19.99/month</span>
              </div>
              <div className={styles.savingsLine}>
                <span>You Save:</span>
                <span className={styles.savingsAmount}>$158/month (89% off!)</span>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Smart Creator Stack Section */}
      {/* <section className={styles.smartStackSection}>
        <div className={styles.smartStackContent}>
          <h3>💡 The Smart Creator Stack</h3>
          <div className={styles.stackComparison}>
            <div className={styles.stackColumn}>
              <h4>What You're Probably Paying:</h4>
              <div className={styles.stackItems}>
                <div className={styles.stackItem}>
                  <span>❌ Captions Tool: $15/mo</span>
                </div>
                <div className={styles.stackItem}>
                  <span>❌ Thumbnails: $9/mo</span>
                </div>
                <div className={styles.stackItem}>
                  <span>❌ Audio Narration: $22/mo</span>
                </div>
                <div className={styles.stackItem}>
                  <span>❌ Script Writing: $49/mo</span>
                </div>
                <div className={styles.stackItem}>
                  <span>❌ Sound Effects: $7/mo</span>
                </div>
                <div className={styles.stackItem}>
                  <span>❌ Voiceovers: $11/mo</span>
                </div>
                <div className={styles.stackItem}>
                  <span>❌ Templates: $7/mo</span>
                </div>
                <div className={styles.stackItem}>
                  <span>❌ Transcription: $15/mo</span>
                </div>
                <div className={styles.stackItem}>
                  <span>❌ Idea Generator: $19/mo</span>
                </div>
              </div>
              <div className={styles.stackTotal}>
                <strong>Total: $154/month</strong>
              </div>
            </div>
            
            <div className={styles.stackVs}>
              <div className={styles.vsIcon}>VS</div>
            </div>
            
            <div className={styles.stackColumn}>
              <h4>What You Actually Need:</h4>
              <div className={styles.stackItems}>
                <div className={styles.stackItem}>
                  <span>✅ All 9 Tools → Included</span>
                </div>
                <div className={styles.stackItem}>
                  <span>✅ Christian AI Context → Included</span>
                </div>
                <div className={styles.stackItem}>
                  <span>✅ Integrated Workflow → Included</span>
                </div>
                <div className={styles.stackItem}>
                  <span>✅ Direct Social Publishing → Included</span>
                </div>
                <div className={styles.stackItem}>
                  <span>✅ Priority Support → Included</span>
                </div>
              </div>
              <div className={styles.stackTotal}>
                <strong>🔥 Kingdom Studio: $19.99/month</strong>
              </div>
              <div className={styles.stackSavings}>
                <strong>Save $134/month. Forever.</strong>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Cost Comparison Banner */}
      {/* <section className={styles.comparisonBanner}>
        <div className={styles.comparisonContent}>
          <h3>Stop Paying for Multiple Tools</h3>
          <div className={styles.costComparison}>
            <div className={styles.competitorCosts}>
              <h4>Traditional Approach:</h4>
              <div className={styles.toolList}>
                <span>Jasper AI (scripts): $49/month</span>
                <span>Canva Pro: $15/month</span>
                <span>Descript: $15/month</span>
                <span>ElevenLabs: $22/month</span>
                <span>Rev.com: $25/hour</span>
                <span>Scriptwriting: $50-100/project</span>
              </div>
              <div className={styles.totalCost}>
                <strong>Total: $200+/month + project costs</strong>
              </div>
            </div>
            
            <div className={styles.vsArrow}>VS</div>
            
            <div className={styles.ourSolution}>
              <h4>Our Platform (Founder Rate):</h4>
              <div className={styles.ourFeatures}>
                <span>✅ All tools above + 4 more</span>
                <span>✅ Script-to-video workflow</span>
                <span>✅ Christian-focused AI</span>
                <span>✅ Integrated workflow</span>
                <span>✅ Direct social publishing</span>
              </div>
              <div className={styles.ourPrice}>
                <strong>Just $19.99-69.99/month</strong>
                <small>(Regular: $24.99-99.99 after 100 founders)</small>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Workflow Integration Section */}
      {/* <section className={styles.workflowSection}>
        <div className={`${styles.sectionHeader} ${styles.workflowHeader}`}>
          <h2>Complete AI-Powered Workflow</h2>
          <p>See how all 9 apps work together to create professional content without recording - 100% AI generated</p>
        </div>
        
        <div className={styles.workflowContainer}>
          <div className={styles.workflowSteps}>
            <div className={styles.workflowStep}>
              <div className={styles.stepNumber}>1</div>
              <div className={styles.stepIcon}>🎬</div>
              <h3>Create Script</h3>
              <p className={styles.stepApp}>AI Script Generator</p>
              <p>Generate complete sermon or content scripts with AI that understands Christian context</p>
              <div className={styles.stepOutput}>
                <strong>Output:</strong> "Finding Peace in Chaos - Complete Sermon Script"
              </div>
              <div className={styles.stepTime}>⏱️ 3 minutes</div>
            </div>

            <div className={styles.workflowArrow}>→</div>

            <div className={styles.workflowStep}>
              <div className={styles.stepNumber}>2</div>
              <div className={styles.stepIcon}>🎯</div>
              <h3>Auto-Convert to Prompts</h3>
              <p className={styles.stepApp}>Script → Prompt Conversion</p>
              <p>Scripts automatically become optimized video prompts and talking points</p>
              <div className={styles.stepOutput}>
                <strong>Output:</strong> Video prompts + structured talking points
              </div>
              <div className={styles.stepTime}>⏱️ 30 seconds</div>
            </div>

            <div className={styles.workflowArrow}>→</div>

            <div className={styles.workflowStep}>
              <div className={styles.stepNumber}>3</div>
              <div className={styles.stepIcon}>🎵</div>
              <h3>Generate Audio Narration</h3>
              <p className={styles.stepApp}>AI Audio Generator</p>
              <p>Create professional AI narration from your script with multiple voice options</p>
              <div className={styles.stepOutput}>
                <strong>Output:</strong> High-quality narration audio file
              </div>
              <div className={styles.stepTime}>⏱️ 2 minutes</div>
            </div>

            <div className={styles.workflowArrow}>→</div>

            <div className={styles.workflowStep}>
              <div className={styles.stepNumber}>4</div>
              <div className={styles.stepIcon}>🎥</div>
              <h3>AI Generated Video</h3>
              <p className={styles.stepApp}>Veo 3 AI Video Generation</p>
              <p>Create professional videos from your script, narration, and prompts using AI</p>
              <div className={styles.stepOutput}>
                <strong>Output:</strong> Complete video with narration
              </div>
              <div className={styles.stepTime}>⏱️ 5 minutes</div>
            </div>

            <div className={styles.workflowArrow}>→</div>

            <div className={styles.workflowStep}>
              <div className={styles.stepNumber}>5</div>
              <div className={styles.stepIcon}>📝</div>
              <h3>Auto-Generate Captions</h3>
              <p className={styles.stepApp}>AI Caption Generator</p>
              <p>AI creates perfect captions from the generated narration with styling options</p>
              <div className={styles.stepOutput}>
                <strong>Output:</strong> Professional captions with 12 style options
              </div>
              <div className={styles.stepTime}>⏱️ 1 minute</div>
            </div>

            <div className={styles.workflowArrow}>→</div>

            <div className={styles.workflowStep}>
              <div className={styles.stepNumber}>6</div>
              <div className={styles.stepIcon}>🔊</div>
              <h3>Enhance with Sound Effects</h3>
              <p className={styles.stepApp}>AI Sound Generator</p>
              <p>Add background music and sound effects to the completed video for final polish</p>
              <div className={styles.stepOutput}>
                <strong>Output:</strong> Enhanced video with background music + effects
              </div>
              <div className={styles.stepTime}>⏱️ 1 minute</div>
            </div>

            <div className={styles.workflowArrow}>→</div>

            <div className={styles.workflowStep}>
              <div className={styles.stepNumber}>7</div>
              <div className={styles.stepIcon}>🖼️</div>
              <h3>Create Thumbnail</h3>
              <p className={styles.stepApp}>AI Thumbnail Creator</p>
              <p>AI analyzes your completed video content to generate eye-catching thumbnails</p>
              <div className={styles.stepOutput}>
                <strong>Output:</strong> 4 professional thumbnail options
              </div>
              <div className={styles.stepTime}>⏱️ 1 minute</div>
            </div>

            <div className={styles.workflowArrow}>→</div>

            <div className={styles.workflowStep}>
              <div className={styles.stepNumber}>8</div>
              <div className={styles.stepIcon}>✍️</div>
              <h3>Optimize Title & Description</h3>
              <p className={styles.stepApp}>Title & Description AI</p>
              <p>Generate SEO-optimized titles and descriptions based on your script content</p>
              <div className={styles.stepOutput}>
                <strong>Output:</strong> 5 title options + full description with tags
              </div>
              <div className={styles.stepTime}>⏱️ 30 seconds</div>
            </div>

            <div className={styles.workflowArrow}>→</div>

            <div className={styles.workflowStep}>
              <div className={styles.stepNumber}>9</div>
              <div className={styles.stepIcon}>📤</div>
              <h3>Publish Everywhere</h3>
              <p className={styles.stepApp}>Social Media Share</p>
              <p>Upload the final enhanced video directly to YouTube, Instagram, Facebook</p>
              <div className={styles.stepOutput}>
                <strong>Output:</strong> Live on all platforms with full audio enhancement
              </div>
              <div className={styles.stepTime}>⏱️ 2 minutes</div>
            </div>
          </div>

          <div className={styles.workflowSummary}>
            <div className={styles.summaryCard}>
              <h3>Traditional Way vs Our Platform</h3>
              <div className={styles.comparison}>
                <div className={styles.traditionalWay}>
                  <h4>❌ Traditional Approach</h4>
                  <ul>
                    <li>Switch between 9+ different tools</li>
                    <li>Manual scriptwriting (2-3 hours)</li>
                    <li>Record video yourself (1-2 hours)</li>
                    <li>Manual audio editing and mixing</li>
                    <li>Manual file uploads/downloads</li>
                    <li>Inconsistent results</li>
                    <li>6-8 hours total time</li>
                    <li>$220+ monthly tool costs</li>
                  </ul>
                </div>
                <div className={styles.ourWay}>
                  <h4>✅ Our AI-Powered Platform</h4>
                  <ul>
                    <li>One platform, 100% AI-generated content</li>
                    <li>AI scriptwriting (3 minutes)</li>
                    <li>AI video generation (5 minutes)</li>
                    <li>AI narration and sound effects</li>
                    <li>Files pass automatically between tools</li>
                    <li>AI-powered consistency</li>
                    <li>16 minutes total time</li>
                    <li>$19.99-69.99 monthly (Founder Rate)</li>
                  </ul>
                </div>
              </div>
              <div className={styles.timeSaved}>
                <strong>Result: Save 6+ hours per video + $200/month (Founder Rate)</strong>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Features Section */}
      <section className={styles.features}>
        <div className={styles.sectionHeader}>
          <h2>{t('features.sectionTitle')}</h2>
          <p>{t('features.sectionSubtitle')}</p>
        </div>
        <div className={styles.featuresGrid}>
          {features.map((feature, index) => (
            <div 
              key={feature.id}
              className={`${styles.featureCard} ${feature.comingSoon ? styles.comingSoon : ''}`}
            >
              <div className={styles.featureIcon}>{feature.icon}</div>
              {feature.comingSoon && (
                <div className={styles.comingSoonBadge}>{t('features.comingSoon')}</div>
              )}
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
              {/* <div className={styles.aiModelBadge}>{feature.aiModel}</div> */}
              <button 
                className={styles.featureButton}
                onClick={feature.action}
                disabled={feature.comingSoon}
              >
                {feature.comingSoon ? t('features.notifyMe') : t('features.tryNow')}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* VIDEO GALLERY SECTION - NEW */}
      <VideoGallery t={t} />

      {/* Stats Section */}
      <section className={styles.stats}>
        <div className={styles.statsGrid}>
          {stats.map((stat, index) => (
            <div key={index} className={styles.statCard}>
              <div className={styles.statNumber}>
                {stat.number}<span className={styles.statSuffix}>{stat.suffix}</span>
              </div>
              <div className={styles.statLabel}>{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className={styles.testimonials}>
        <div className={styles.sectionHeader}>
          <h2>{t('testimonials.sectionTitle')}</h2>
          <p>{t('testimonials.sectionSubtitle')}</p>
        </div>
        <div className={styles.testimonialsGrid}>
          {testimonials.map((testimonial, index) => (
            <div key={index} className={styles.testimonialCard}>
              <div className={styles.testimonialHeader}>
                <div className={styles.avatar}>{testimonial.avatar}</div>
                <div>
                  <h4>{testimonial.name}</h4>
                  <p>{testimonial.role}</p>
                </div>
                <div className={styles.metric}>{testimonial.metric}</div>
              </div>
              <blockquote>"{testimonial.quote}"</blockquote>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className={styles.finalCTA}>
        <div className={styles.ctaContent}>
          <h2>{t('finalCTA.title')}</h2>
          <p>{t('finalCTA.subtitle')}</p>
          <div className={styles.ctaButtons}>
            <button 
              className={styles.primaryButton}
              onClick={() => navigate('/create-content')}
            >
              {t('finalCTA.cta')} - {t('finalCTA.noCredit')}
            </button>
            <button 
              className={styles.secondaryButton}
              onClick={() => navigate('/pricing')}
            >
              {t('finalCTA.title')}
            </button>
          </div>
          <div className={styles.ctaFeatures}>
            <span>✅ {t('finalCTA.noCredit')}</span>
            <span>✅ {t('finalCTA.cancelAnytime')}</span>
            <span>✅ {t('hero.moneyBackGuarantee')}</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EnhancedHome;